import { ReactNode } from 'react'
import { MoreVertical } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export type TableRowActionOption = {
  id?: string
  text: string
  key?: string
  icon?: ReactNode
  disabled?: boolean
  active?: boolean
  asc?: boolean
}

export interface TableRowActionsProps<TData = unknown> {
  /** The menu options to render */
  options: TableRowActionOption[]
  /** Row data passed back in the click callback */
  data?: TData
  /** Menu header title */
  title?: string
  /** Callback when a menu item is clicked */
  onMoreOptionClick: (option: TableRowActionOption, data: TData) => void
  /** Optional header icon rendered beside the title */
  headerIcon?: ReactNode
  /** Optional custom trigger element (replaces the default "..." button) */
  children?: ReactNode
}

/**
 * Kebab-menu of per-row actions for table rows (edit/delete/etc.), built on DropdownMenu.
 *
 * @category Actions
 */
export function TableRowActions<TData = unknown>({
  options,
  data,
  title,
  onMoreOptionClick,
  headerIcon,
  children,
}: TableRowActionsProps<TData>) {
  const handleItemClick = (option: TableRowActionOption) => {
    if (option.disabled) return
    onMoreOptionClick(option, data as TData)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button variant="ghost" size="icon" className="h-8 w-8 max-lg:h-11 max-lg:w-11">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open Menu</span>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {title && (
          <>
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>{title}</span>
              {headerIcon && <span>{headerIcon}</span>}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id || option.text}
            disabled={option.disabled}
            className={option.active ? 'bg-accent' : ''}
            onSelect={() => handleItemClick(option)}
          >
            {option.icon && (
              <span className="mr-2 flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">
                {option.icon}
              </span>
            )}
            {option.text}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
